import React from 'react';
import { Keyboard, X } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper to format key combos for display
const formatKeyCombo = (combo: string): React.ReactNode[] => {
  // First check if it contains alternative keys separated by slash
  if (combo.includes('/')) {
    const alternatives = combo.split('/').map(alt => alt.trim());
    const result: React.ReactNode[] = [];

    alternatives.forEach((alt, altIndex) => {
      // Add each alternative's formatted keys
      const formattedAlt = formatSingleKeyCombo(alt);
      result.push(...formattedAlt);

      // Add "or" separator between alternatives, but not after the last one
      if (altIndex < alternatives.length - 1) {
        result.push(
          <span key={`sep-${altIndex}`} className="mx-2 text-gray-400">
            or
          </span>
        );
      }
    });

    return result;
  }

  // Otherwise format as a single key combo
  return formatSingleKeyCombo(combo);
};

// Format a single key combination (without alternatives)
const formatSingleKeyCombo = (combo: string): React.ReactNode[] => {
  const keys = combo.split('+');

  return keys.map((key, index) => {
    let displayKey = key;

    // Convert special keys to symbols or more readable names
    switch (key) {
      case 'ctrl':
        displayKey = 'Ctrl';
        break;
      case 'alt':
        displayKey = 'Alt';
        break;
      case 'shift':
        displayKey = 'Shift';
        break;
      case 'meta':
        displayKey = navigator.platform.includes('Mac') ? '⌘' : 'Win';
        break;
      case 'escape':
        displayKey = 'Esc';
        break;
      case 'arrowup':
        displayKey = '↑';
        break;
      case 'arrowdown':
        displayKey = '↓';
        break;
      case 'arrowleft':
        displayKey = '←';
        break;
      case 'arrowright':
        displayKey = '→';
        break;
      default:
        // Capitalize single letter keys
        if (key.length === 1) {
          displayKey = key.toUpperCase();
        }
    }

    return (
      <React.Fragment key={`${combo}-${index}`}>
        <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded shadow-sm border border-gray-300 font-mono text-sm">
          {displayKey}
        </kbd>
        {index < keys.length - 1 && <span className="mx-1">+</span>}
      </React.Fragment>
    );
  });
};

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
  // Define our known shortcuts manually
  const shortcuts = [
    {
      id: 'toggle_sidebars',
      combo: 'p / a',
      description: 'Toggle sidebar visibility',
      scope: 'global',
      note: '(Desktop only)',
    },
    {
      id: 'switch_projects',
      combo: 'tab',
      description: 'Switch between current project and recently used project',
      scope: 'global',
      note: '(Desktop only)',
    },
    {
      id: 'go_to_dashboard',
      combo: 'd',
      description: 'Go to dashboard',
      scope: 'global',
      note: '(Desktop only)',
    },
  ];

  // Check if we're in a Capacitor environment
  const isCapacitorApp =
    typeof window !== 'undefined' &&
    window.Capacitor &&
    window.Capacitor.isNativePlatform &&
    window.Capacitor.isNativePlatform();

  // If dialog is not open or we're in Capacitor, don't render anything
  if (!isOpen || isCapacitorApp) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Keyboard size={20} className="text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Shortcut content - simplified */}
        <div className="p-6">
          <div className="space-y-4 mb-6">
            {shortcuts.map(shortcut => (
              <div key={shortcut.id} className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-800 font-medium">
                    {shortcut.description}
                    {shortcut.note && (
                      <span className="text-gray-500 text-sm italic ml-1">{shortcut.note}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  {formatKeyCombo(shortcut.combo)}
                </div>
              </div>
            ))}
          </div>

          {/* Coming soon section - more prominent */}
          <div className="text-center py-4 bg-blue-50 rounded-lg">
            <p className="text-blue-700">
              Need a specific shortcut? More keyboard shortcuts coming soon!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
